import { formatMailbox, parseAddressList, parseMailbox, type Mailbox } from './address.ts';
import type { MessageDetail } from './types.ts';

export function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/** Plain text (as the AI writes it) to simple paragraph HTML. */
export function plainToHtml(text: string): string {
  return text
    .replace(/\r\n/g, '\n')
    .trim()
    .split(/\n{2,}/)
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

/** Lightweight HTML to text without a DOM (good enough for previews, prompts and text/plain parts). */
export function htmlToPlain(html: string): string {
  return decodeEntities(
    html
      .replace(/<(script|style|head)[^>]*>[\s\S]*?<\/\1>/gi, ' ')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|h\d|li|tr|blockquote)>/gi, '\n')
      .replace(/<li[^>]*>/gi, '• ')
      .replace(/<[^>]+>/g, ''),
  )
    .replace(/[ \t ]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function decodeEntities(s: string): string {
  const named: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', hellip: '…', mdash: '—', ndash: '–', rsquo: '’', lsquo: '‘', rdquo: '”', ldquo: '“' };
  return s.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (m, e: string) => {
    if (e[0] === '#') {
      const code = e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
      return Number.isFinite(code) ? String.fromCodePoint(code) : m;
    }
    return named[e.toLowerCase()] ?? m;
  });
}

export function replySubject(subject: string): string {
  return /^\s*re\s*:/i.test(subject) ? subject : `Re: ${subject}`;
}

export function forwardSubject(subject: string): string {
  return /^\s*(fwd?|fw)\s*:/i.test(subject) ? subject : `Fwd: ${subject}`;
}

/** Recipients for reply / reply-all, excluding the user's own addresses. */
export function replyRecipients(d: MessageDetail, myAddresses: string[], all: boolean): { to: Mailbox[]; cc: Mailbox[] } {
  const mine = new Set(myAddresses.map((a) => a.toLowerCase()));
  const outbound = d.summary.direction === 'outbound' || mine.has(d.summary.fromAddress.toLowerCase());
  const primary = outbound
    ? parseAddressList(d.summary.to)
    : parseAddressList(d.replyTo).length
      ? parseAddressList(d.replyTo)
      : [parseMailbox(d.summary.from) ?? { address: d.summary.fromAddress }];
  const to = primary.filter((m) => !mine.has(m.address));
  if (!all) return { to: to.length ? to : primary, cc: [] };
  const seen = new Set(to.map((m) => m.address));
  const cc = [...(outbound ? [] : parseAddressList(d.summary.to)), ...parseAddressList(d.summary.cc)].filter((m) => {
    if (mine.has(m.address) || seen.has(m.address)) return false;
    seen.add(m.address);
    return true;
  });
  return { to, cc };
}

export function quoteHeader(d: MessageDetail): string {
  const when = new Date(d.summary.sentDate ?? d.summary.receivedUtc);
  const date = when.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  return `On ${date}, ${d.summary.from || d.summary.fromAddress} wrote:`;
}

/** The quoted original appended under a reply. */
export function quotedOriginalHtml(d: MessageDetail, bodyHtml: string): string {
  return `<br><div class="dts-quote"><p style="color:#6b7280;margin:0 0 6px">${escapeHtml(quoteHeader(d))}</p>` +
    `<blockquote style="margin:0 0 0 .8ex;border-left:2px solid #c7c7d1;padding-left:1ex">${bodyHtml}</blockquote></div>`;
}

export function forwardedHtml(d: MessageDetail, bodyHtml: string): string {
  const s = d.summary;
  const rows = [
    ['From', s.from],
    ['Date', new Date(s.sentDate ?? s.receivedUtc).toLocaleString()],
    ['Subject', s.subject],
    ['To', s.to],
    ...(s.cc ? [['Cc', s.cc]] : []),
  ].map(([k, v]) => `${k}: ${escapeHtml(v ?? '')}`).join('<br>');
  return `<br><div class="dts-forward"><p>---------- Forwarded message ----------<br>${rows}</p>${bodyHtml}</div>`;
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10240 ? 1 : 0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export function mailboxesToStrings(list: Mailbox[]): string[] {
  return list.map(formatMailbox);
}

/** Splits a compose-mode AI result ("Subject: ...\n\nBody") into its parts. */
export function splitSubject(text: string): { subject?: string; body: string } {
  const m = text.match(/^\s*subject\s*:\s*(.*)\n/i);
  if (!m) return { body: text };
  return { subject: m[1].trim(), body: text.slice(m[0].length).replace(/^\s*\n/, '') };
}

/** Places a message body into a domain template ({{content}}, {{sender_name}}, {{sender_email}}, {{domain}}, {{year}}). */
export function applyTemplate(templateHtml: string, vars: { content: string; senderName?: string; senderEmail?: string; domain?: string }): string {
  const fill: Record<string, string> = {
    sender_name: escapeHtml(vars.senderName ?? ''),
    sender_email: escapeHtml(vars.senderEmail ?? ''),
    domain: escapeHtml(vars.domain ?? ''),
    year: String(new Date().getFullYear()),
  };
  return templateHtml
    .replace(/\{\{\s*(sender_name|sender_email|domain|year)\s*\}\}/g, (_m, k: string) => fill[k] ?? '')
    .replace(/\{\{\s*content\s*\}\}/, () => vars.content);
}

/** Sample body used to preview templates. */
export const TEMPLATE_SAMPLE_BODY =
  '<p>Hi Alex,</p><p>Thanks for meeting with us this week. As promised, here is a quick recap of the next steps for the project, ' +
  'along with the <a href="https://example.com">proposal link</a>.</p><ul><li>Finalize the scope by Friday</li><li>Kick-off call next Tuesday</li></ul>' +
  '<p>Let me know if anything changes.</p><p>Best,<br>Chuck</p>';
